import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from './store';
import http from '../utils/http';
import { logerUser, NewUser } from './UserSlice';


export interface NewSignupUser {
    firstName: string;
    lastName: string;
    eMail: string;
    role: string;
}


export interface B2CUser {
    displayName: string;
    signInEmail: string;
    email: string;
    id: string;
}

export interface RegistrationState {
    newUser: NewSignupUser | null;
    newUserRegistered: boolean;
    userCheck: B2CUser | null;
    status: 'idle' | 'loading' | 'failed';
}

const initialState: RegistrationState = {
    newUser: null,
    newUserRegistered: false,
    userCheck: null,
    status: 'idle'
}

export const registerUser = createAsyncThunk(
    "Registration/registerUser",
    async (newUser: NewSignupUser): Promise<boolean | null> => {
        try {
            const result = await http<NewSignupUser, boolean>({
                path: `/registration/registerUser`,
                method: 'post',
                body: newUser
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    }
);

export const verifyUsernameExists = createAsyncThunk(
    "Registration/verifyUsernameExists",
    async (username: string): Promise<boolean | null> => {
        try {
            const result = await http<string, boolean>({
                path: `/registration/verifyUsernameExists`,
                method: 'post',
                body: username
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    }
)

export const getB2CUserInfo = createAsyncThunk(
    "Registration/getB2CUserInfo",
    async (username: string): Promise<boolean | null> => {
        try {
            const result = await http<string, boolean>({
                path: `/registration/getB2CUserInfo`,
                method: 'post',
                body: username
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    }
)

export const deactivateUser = createAsyncThunk(
    "Registration/DeactivateUser",
    async (userId: string): Promise<logerUser | null> => {
        try {

            const result = await http<string, logerUser>({
                path: `/registration/deactivateUser`,
                method: 'post',
                body: userId
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });

export const registrationSlice = createSlice({
    name: 'Registration',
    initialState,
    reducers: {
        clearState(state: any) {
            state = initialState;
        },
    },
    extraReducers: (builder: any) => {
        builder
            .addCase(registerUser.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(registerUser.fulfilled, (state: any, action: any) => {
                state.status = "idle";
            })
            .addCase(verifyUsernameExists.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(verifyUsernameExists.fulfilled, (state: any, action: any) => {
                //todo mutate state
                if (action.payload) {

                    state.newUserRegistered = true;

                }
                state.status = "idle";
            })

            .addCase(deactivateUser.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(deactivateUser.fulfilled, (state: any, action: any) => {
                state.status = "idle";
            })

            .addCase(getB2CUserInfo.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(getB2CUserInfo.fulfilled, (state: any, action: any) => {
                state.status = "idle";
                if (action.payload) {
                    state.userCheck = action.payload;
                }
                else {
                    state.userCheck = null;
                }
            })
    }

});

export const { clearState } = registrationSlice.actions;

export const selectNewUser = (state: RootState) => state.registration.newUser;
export const selectRegistrationStatus = (state: RootState) => state.registration.status;
export const selectUserRegistered = (state: RootState) => state.registration.newUserRegistered;
export const selectUserCheck = (state: RootState) => state.registration.userCheck;

export default registrationSlice.reducer;